/**
 * What a saved alert is allowed to say, and whether one can be saved at all.
 *
 * Here rather than inside the components for the same reason as
 * `reportStatus.ts` and `route.ts`: this is product copy plus a rule, a paying
 * subscriber reads it, and it should be testable without a browser.
 *
 * The rule this module exists to hold is the wording. **We only ever know when
 * *we* first held a parcel.** The counties do not publish a filing date on
 * every row, and several of the feeds behind this product are registers of
 * open cases with no history at all. So an alert may say "new in Distress
 * Signal", and may never say "newly filed", "just reported" or anything else
 * that describes the county's clock rather than ours. The difference matters
 * more here than anywhere else in the app: a page is something the user went
 * and looked at, an alert is a claim delivered to them.
 */

export interface AlertReadiness {
  allowed: boolean;
  /** Why not, for the button's title and for text beside it. Empty when allowed. */
  reason: string;
}

const ALLOWED: AlertReadiness = { allowed: true, reason: "" };

/** The most saved alerts one account may hold. Mirrors the server's own cap. */
export const MAX_WATCHES = 25;

/**
 * Whether "save this search as an alert" can succeed, decided before the click.
 *
 * The rail shows the control to everyone — seeing what a paid plan unlocks is
 * the point of a preview — so this has to explain a refusal rather than the
 * button simply not being there.
 */
export function alertReadiness(
  canWatch: boolean,
  existing: number,
  name: string,
): AlertReadiness {
  if (!canWatch) {
    return {
      allowed: false,
      reason: "Saved alerts start on the County plan.",
    };
  }
  if (existing >= MAX_WATCHES) {
    return {
      allowed: false,
      reason:
        `You already have ${MAX_WATCHES} saved alerts. Delete one to add ` +
        `another.`,
    };
  }
  if (!name.trim()) {
    return { allowed: false, reason: "Give this alert a name first." };
  }
  return ALLOWED;
}

/**
 * The headline for a watch's count.
 *
 * Never "3 new violations" — the count is of rows that entered *our* dataset,
 * and a building can enter it because a case was filed, because a county fixed
 * a record, or because we widened an ingest. "New in Distress Signal" is true
 * in all three.
 */
export function newCountLabel(count: number, scope: string): string {
  const noun = scope === "units" ? "parcel" : "building";
  if (count === 0) return `Nothing new`;
  return `${count.toLocaleString()} new ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * The line under a watch, naming the moment it is measuring from.
 *
 * An empty result has to say what it is empty *since*, or "Nothing new" reads
 * as "there is nothing" — which is a statement about the county rather than
 * about a date, and a false one.
 */
export function measuredFrom(acked: string | null | undefined): string {
  const when = formatWhen(acked);
  return when
    ? `New in Distress Signal since ${when}`
    : "New in Distress Signal since this alert was created";
}

/**
 * Why a watch is showing nothing, or "" when it is showing something.
 *
 * `entitled` false is not an error and not an empty result: the account no
 * longer covers the county this alert watches. The watch is kept rather than
 * deleted — a lapsed card is usually temporary — so the UI has to explain a
 * third state rather than the usual two.
 */
export function emptyReason(
  entitled: boolean,
  serverReason: string,
  count: number,
  acked: string | null | undefined,
): string {
  if (!entitled) {
    return serverReason || "Your plan no longer covers the county this alert watches.";
  }
  if (count > 0) return "";
  return `${measuredFrom(acked)}. Nothing has appeared since.`;
}

/** A short date for the UI. Invalid or absent input yields "". */
export function formatWhen(value: string | null | undefined): string {
  if (!value) return "";
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return "";
  return when.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * A default name for an alert saved off the current filters.
 *
 * Named after what it watches so a list of them is readable. The county name
 * is passed in rather than looked up here, because this module has no business
 * knowing the registry.
 */
export function suggestName(countyName: string, describing: string[]): string {
  const where = countyName ? `${countyName}` : "All counties";
  const what = describing.filter(Boolean).join(", ");
  return what ? `${where} — ${what}` : where;
}

/**
 * The name an alert is actually saved under: what was typed, or the suggestion
 * when nothing was.
 *
 * Split out because the obvious way to write it is a bug. The field used to be
 * `value={typed || suggested}` on a controlled input, which cannot be cleared:
 * emptying it sets the state to "", the fallback immediately refills the box,
 * and the only way to replace the suggestion is to backspace through it one
 * character at a time. A production alert ended up named "All countie" — the
 * suggestion, one keystroke into being deleted.
 *
 * So the suggestion belongs in the `placeholder`, where it can be typed over,
 * and the fallback belongs here, at the moment of saving. Whitespace counts as
 * nothing typed: an alert named " " is unreadable in a list and no one means it.
 */
export function resolveAlertName(typed: string, suggested: string): string {
  return typed.trim() || suggested;
}

/**
 * Human descriptions of the filters a watch will carry.
 *
 * Shown at save time so nobody saves an alert without seeing what it actually
 * matches — the commonest way to end up with a standing query that reports
 * everything, forever.
 */
export function describeFilters(filters: {
  milestone_only?: boolean;
  unsafe_only?: boolean;
  min_score?: number;
  min_rate?: number;
  flag?: string;
  search?: string;
}, scope: string): string[] {
  const out: string[] = [];
  if (scope === "buildings") {
    if (filters.milestone_only) out.push("milestone overdue");
    if (filters.unsafe_only) out.push("unsafe-structure case");
    if (filters.min_rate) out.push(`${Math.round(filters.min_rate * 100)}%+ distressed`);
  } else if (filters.flag) {
    out.push(filters.flag.replace(/_/g, " "));
  }
  if (filters.min_score) out.push(`score ${filters.min_score}+`);
  if (filters.search?.trim()) out.push(`“${filters.search.trim()}”`);
  return out;
}

